/**
 * SIP Authentication Schemas - Browser-Safe
 * 
 * Zod schemas for SIP Digest Authentication (RFC 3261/2617).
 * These schemas can be safely imported in browser environments.
 * 
 * Authentication realm is derived from the linked Host's domain, providing:
 * - Single source of truth for authentication domain
 * - Automatic consistency between SIP identity and auth realm
 * - Simplified configuration (select host instead of typing realm)
 * 
 * @packageDocumentation
 */

import { z } from 'zod';

// ========================================
// SIP AUTH CREDENTIAL INFO (Read Response)
// ========================================

/**
 * SIP Authentication Credential - returned from API
 * This represents the data structure returned when reading credentials
 * 
 * The `realm` field is derived from the linked host's domain for display,
 * while `hostId` is the actual foreign key reference.
 */
export const SipAuthCredentialSchema = z.object({
  id: z.string().uuid(),
  authId: z.string(),
  username: z.string(),
  // Host reference - links to sip_host table
  hostId: z.string().uuid(),
  // Derived field - the host's domain used as the authentication realm
  // This is included for display purposes and backward compatibility
  realm: z.string(),
  // Host name for display
  hostName: z.string().optional(),
  algorithm: z.enum(['MD5', 'SHA-256']),
  enabled: z.boolean(),
  peerId: z.string().nullable().optional(),
  successfulAuths: z.number(),
  failedAttempts: z.number(),
  lastSuccessfulAuth: z.string().nullable().optional(),
  lastAuthAttempt: z.string().nullable().optional(),
  lockedUntil: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SipAuthCredential = z.infer<typeof SipAuthCredentialSchema>;

// ========================================
// CREATE CREDENTIAL INPUT
// ========================================

/**
 * Input schema for creating SIP authentication credentials
 * 
 * Uses hostId instead of realm - the realm is derived from the host's domain.
 */
export const SipAuthCredentialCreateInputSchema = z.object({
  username: z.string().min(1, 'Username is required').max(100, 'Username must be 100 characters or less'),
  password: z.string().min(6, 'Password must be at least 6 characters').max(100, 'Password must be 100 characters or less'),
  // Host reference - the host's domain will be used as the authentication realm
  hostId: z.string().uuid('Host is required'),
  // Omitted values retain the legacy MD5 provisioning policy.
  algorithm: z.enum(['MD5', 'SHA-256']).default('MD5'),
  peerId: z.string().optional(),
  enabled: z.boolean().default(true),
  metadata: z.record(z.string(), z.any()).optional(),
});

export type SipAuthCredentialCreateInput = z.infer<typeof SipAuthCredentialCreateInputSchema>;

// ========================================
// UPDATE CREDENTIAL INPUT
// ========================================

/**
 * Input schema for updating SIP authentication credentials
 * 
 * Note: Changing hostId will require re-computing the HA1 hash since realm changes.
 */
export const SipAuthCredentialUpdateInputSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters').max(100).optional(),
  algorithm: z.enum(['MD5', 'SHA-256']).optional(),
  // Changing host changes the realm, requiring password re-hash
  hostId: z.string().uuid().optional(),
  peerId: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

export type SipAuthCredentialUpdateInput = z.infer<typeof SipAuthCredentialUpdateInputSchema>;

// ========================================
// QUERY PARAMETERS
// ========================================

/**
 * Query parameters for listing credentials
 */
export const SipAuthCredentialQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(1000).default(50),
  offset: z.coerce.number().min(0).default(0),
  // Filter by host ID
  hostId: z.string().uuid().optional(),
  enabled: z.coerce.boolean().optional(),
});

export type SipAuthCredentialQuery = z.infer<typeof SipAuthCredentialQuerySchema>;

// ========================================
// STATISTICS
// ========================================

/**
 * SIP Authentication statistics
 */
export const SipAuthStatsSchema = z.object({
  totalCredentials: z.number(),
  activeCredentials: z.number(),
  lockedCredentials: z.number(),
  totalSuccessfulAuths: z.number(),
  totalFailedAttempts: z.number(),
});

export type SipAuthStats = z.infer<typeof SipAuthStatsSchema>;

// ========================================
// CLEANUP RESULT
// ========================================

/**
 * Result from cleanup operation
 */
export const SipAuthCleanupResultSchema = z.object({
  expiredNonces: z.number(),
  unlockedAccounts: z.number(),
  duration: z.number(),
});

export type SipAuthCleanupResult = z.infer<typeof SipAuthCleanupResultSchema>;
